import type { LucideIcon } from 'lucide-react'

/**
 * F-198: how a space lays out its items, chosen per space by owner and admin.
 * The values of the API's `Space.layout` enum; `null` on the space means the
 * module's default (what the module always drew).
 *
 * `showcase` ("Vitrine") is drawn by Courses only, since F-191 phase 3.
 */
export type SpaceLayout = 'feed' | 'cards' | 'list' | 'compact' | 'grid' | 'showcase' | 'calendar'

export const SPACE_LAYOUTS: SpaceLayout[] = ['feed', 'cards', 'list', 'compact', 'grid', 'showcase', 'calendar']

export const isSpaceLayout = (value: unknown): value is SpaceLayout =>
  typeof value === 'string' && (SPACE_LAYOUTS as string[]).includes(value)

/** What `GET /api/spaces/modules` says a module offers (`layouts: { options, default }`). */
export interface ModuleLayouts {
  options: SpaceLayout[]
  default: SpaceLayout
}

/**
 * One item as the List and Compact layouts draw it (F-198, objection 1): the
 * six modules share no card, so each module turns its item into this row.
 */
export interface ItemRow {
  id: string
  title: string
  /** The item's page; the whole row opens it. */
  href?: string | null
  /** Opens the item in place (the Gallery's dialog) when there is no page. */
  onOpen?: () => void
  /** Small picture of List; Compact never draws it. */
  thumbnail?: string | null
  /** Drawn in place of the thumbnail when there is none. */
  icon?: LucideIcon
  /** The two lines of metadata under the title (List). */
  meta?: Array<string | null | undefined>
  /** The one datum that decides (date, price, deadline): the right side of Compact, a chip in List. */
  decisive?: string | null
  /** Drawn with the padlock of F-161, with its reason as the label. */
  locked?: boolean
  lockLabel?: string | null
  /** A short status the team reads (draft, pending). */
  badge?: string | null
}
